﻿import { flatMap, groupBy, keyBy, partition } from 'es-toolkit/array';
import { action, computed, makeObservable, observable } from 'mobx';
import { maximumNumberValue } from '../../common/number/maximum-number-value';
import { Award, AwardGroup, AwardGroupRef, AwardRequest, GroupRequest, MasterAward, MasterAwardRequest } from '../../models';
import { AwardOperation, getAwardRequestSchema } from '../../models/schemas/award-request.schema';
import { groupRequestSchema } from '../../models/schemas/group-request.schema';
import { masterAwardRequestSchema } from '../../models/schemas/master-award-request.schema';
import { createValidationState, ValidationState } from '../../shared/validation';

type ModalType = 'add' | 'update' | undefined;

export class ManageAwardListPageUiStore {
  @observable addOrEditGroupModalType?: ModalType;

  @observable addOrEditAwardModalType?: ModalType;

  @observable addOrEditMasterAwardModalType?: ModalType;

  @observable originalAwardGroups: AwardGroup[] = [];

  @observable addedGroups: AwardGroup[] = [];
  @observable updatedGroupMap: Map<number, AwardGroup> = new Map();
  @observable deletedGroupIds: Set<number> = new Set();

  @observable addedAwards: AwardGroupRef[] = [];
  @observable updatedAwardMap: Map<number, AwardGroupRef> = new Map();
  @observable deletedAwardIds: Set<number> = new Set();

  @observable editingAwardId?: number;

  @observable masterAwards: MasterAward[] = [];

  @observable selectedMasterAwardId?: number;

  @observable masterAwardRequestBody?: MasterAwardRequest;

  @observable selectedAward?: Award;

  @observable groupRequestBody?: GroupRequest;

  @observable editingGroupId?: number;

  @observable selectedAwardGroup?: AwardGroup;

  @observable awardRequestBody?: AwardRequest;

  @observable selectedMasterAwardTitle?: string;

  @observable selectedAwardGroupRef?: AwardGroupRef;

  @observable currentProgramId?: number;

  constructor() {
    makeObservable(this);
  }

  @action
  resetStates(): void {
    this.addOrEditGroupModalType = undefined;
    this.addOrEditAwardModalType = undefined;
    this.addOrEditMasterAwardModalType = undefined;

    this.originalAwardGroups = [];
    this.addedGroups = [];
    this.updatedGroupMap = new Map();
    this.deletedGroupIds = new Set();
    this.addedAwards = [];
    this.updatedAwardMap = new Map();
    this.deletedAwardIds = new Set();

    this.masterAwards = [];
    this.selectedMasterAwardId = undefined;
    this.masterAwardRequestBody = undefined;

    this.selectedAward = undefined;
    this.groupRequestBody = undefined;
    this.editingGroupId = undefined;
    this.selectedAwardGroup = undefined;
    this.editingAwardId = undefined;
    this.awardRequestBody = undefined;
    this.selectedMasterAwardTitle = undefined;
    this.selectedAwardGroupRef = undefined;
    this.currentProgramId = undefined;
  }

  @action
  setAddOrEditGroupModalType(modalType?: ModalType): void {
    this.addOrEditGroupModalType = modalType;
  }

  @action
  setAddOrEditAwardModalType(modalType?: ModalType): void {
    this.addOrEditAwardModalType = modalType;
  }

  @action
  setAddOrEditMasterAwardModalType(modalType?: ModalType): void {
    this.addOrEditMasterAwardModalType = modalType;
  }

  @action
  setCurrentProgramId(programId: number): void {
    this.currentProgramId = programId;
  }

  @action
  setMasterAwards(masterAwards: MasterAward[]): void {
    this.masterAwards = masterAwards;
  }

  @action
  setAwardGroups(awardGroups: AwardGroup[]): void {
    this.originalAwardGroups = awardGroups;
    this.addedGroups = [];
    this.updatedGroupMap = new Map();
    this.deletedGroupIds = new Set();
    this.addedAwards = [];
    this.updatedAwardMap = new Map();
    this.deletedAwardIds = new Set();
  }

  @computed
  get awardGroups(): AwardGroup[] {
    const updatedAwardById = keyBy([...this.updatedAwardMap.values()], (award) => award.awardId);

    const addedAwardsByGroupId = groupBy(this.addedAwards, (award) => award.groupId);

    const originalMapped = this.originalAwardGroups.map((group) => {
      const updatedGroup = this.updatedGroupMap.get(group.awardGroupId);
      const base = updatedGroup ?? group;

      return {
        ...base,
        awards: [...base.awards.map((award) => updatedAwardById[award.awardId] ?? award), ...(addedAwardsByGroupId[base.awardGroupId] ?? [])],
      };
    });

    const addedMapped = this.addedGroups.map((group) => ({
      ...group,
      awards: [...group.awards, ...(addedAwardsByGroupId[group.awardGroupId] ?? [])],
    }));

    return [...originalMapped, ...addedMapped];
  }

  @computed
  get availableAwardGroups(): AwardGroup[] {
    return this.awardGroups.filter((group) => !this.deletedGroupIds.has(group.awardGroupId));
  }

  @computed
  get availableMasterAwards(): MasterAward[] {
    const usedMasterAwardIds = new Set(this.addedAwards.map((award) => award.masterAwardId));
    return this.masterAwards.filter((masterAward) => !usedMasterAwardIds.has(masterAward.masterAwardId));
  }

  @action
  createSelectedAwardFromRef(awardGroupRef: AwardGroupRef): void {
    const award: Award = {
      awardId: awardGroupRef.awardId,
      masterAwardId: 0,
      groupId: 0,
      summary: '',
      detail: '',
      requireWrittenNomination: false,
      requireCustomerReference: false,
      allowJointNomination: false,
      title: awardGroupRef.title,
      isCountryAward: false,
      isRegionAward: false,
      lastUpdatedDate: undefined,
      judgeCapacity: maximumNumberValue,
      maxJudgementCount: 3,
      maxWinners: 1,
      maxFinalists: 3,
      eligibilityRuleCodes: [],
    };

    this.selectedAward = award;
  }

  @action
  createGroupRequestBody(): void {
    if (!this.currentProgramId) return;

    this.editingGroupId = undefined;

    const initialValue: GroupRequest = {
      programId: this.currentProgramId,
      displayName: '',
    };

    this.groupRequestBody = initialValue;
  }

  @action
  createGroupRequestBodyForUpdate(existingGroup: AwardGroup): void {
    if (!this.currentProgramId) return;

    this.editingGroupId = existingGroup.awardGroupId;

    const initialValue: GroupRequest = {
      programId: this.currentProgramId,
      displayName: existingGroup.title,
    };

    this.groupRequestBody = initialValue;
  }

  @action
  addNewGroup(): void {
    if (!this.groupRequestBody) return;

    var awardGroup: AwardGroup = {
      awardGroupId: this.nextTemporaryId(),
      title: this.groupRequestBody.displayName,
      awards: [],
      status: 'Added',
    };

    this.addedGroups = [...this.addedGroups, awardGroup];
  }

  @action
  updateGroup(): void {
    if (!this.groupRequestBody || this.editingGroupId === undefined) return;
    const body = this.groupRequestBody;
    const groupId = this.editingGroupId;

    const addedIdx = this.addedGroups.findIndex((awardGroup) => awardGroup.awardGroupId === groupId);
    if (addedIdx !== -1) {
      this.addedGroups = this.addedGroups.map((awardGroup) => {
        if (awardGroup.awardGroupId === groupId) {
          return {
            ...awardGroup,
            title: body.displayName,
          };
        }
        return awardGroup;
      });
      return;
    }

    const original = this.originalAwardGroups.find((awardGroup) => awardGroup.awardGroupId === groupId);
    if (!original) return;
    this.updatedGroupMap = new Map(this.updatedGroupMap);
    this.updatedGroupMap.set(groupId, {
      ...original,
      title: body.displayName,
    });
  }

  @action
  deleteAwardGroup(): void {
    if (!this.selectedAwardGroup) return;
    const groupId = this.selectedAwardGroup.awardGroupId;

    if (this.addedGroups.some((group) => group.awardGroupId === groupId)) {
      this.addedGroups = this.addedGroups.filter((group) => group.awardGroupId !== groupId);
      this.addedAwards = this.addedAwards.filter((award) => award.groupId !== groupId);
    } else {
      this.updatedGroupMap = new Map(this.updatedGroupMap);
      this.updatedGroupMap.delete(groupId);
      this.deletedGroupIds = new Set(this.deletedGroupIds).add(groupId);
    }

    this.selectedAwardGroup = undefined;
  }

  @action
  replaceTemporaryGroupId(temporaryGroupId: number, groupId: number): void {
    this.addedAwards = this.addedAwards.map((award) => (award.groupId === temporaryGroupId ? { ...award, groupId } : award));
  }

  @action
  setSelectedAwardGroup(selectedAwardGroup?: AwardGroup) {
    this.selectedAwardGroup = selectedAwardGroup;
  }

  @action
  setSelectedAwardGroupRef(selectedAwardGroupRef?: AwardGroupRef): void {
    this.selectedAwardGroupRef = selectedAwardGroupRef;
  }

  @action
  createAwardRequestBody(): void {
    if (!this.currentProgramId) return;

    this.editingAwardId = undefined;
    this.selectedMasterAwardTitle = undefined;

    const initialValue: AwardRequest = {
      programId: this.currentProgramId,
      masterAwardId: 0,
      groupId: 0,
      title: '',
      summary: '',
      detail: '',
      requireWrittenNomination: false,
      requireCustomerReference: false,
      allowJointNomination: false,
      eligibilityRuleCodes: [],
    };

    this.awardRequestBody = initialValue;
  }

  @action
  createAwardRequestBodyForUpdate(existing: AwardGroupRef): void {
    this.editingAwardId = existing.awardId;
    this.selectedMasterAwardTitle = existing.masterAwardTitle;
    this.selectedMasterAwardId = existing.masterAwardId;

    const initialValue: AwardRequest = {
      programId: existing.programId,
      masterAwardId: existing.masterAwardId,
      groupId: existing.groupId,
      title: existing.title,
      summary: existing.summary,
      detail: existing.detail,
      requireWrittenNomination: existing.requireWrittenNomination,
      requireCustomerReference: existing.requireCustomerReference,
      allowJointNomination: existing.allowJointNomination,
      eligibilityRuleCodes: existing.eligibilityRuleCodes,
    };

    this.awardRequestBody = initialValue;
  }

  @action
  addNewAward(): void {
    if (!this.awardRequestBody) return;

    const masterAward = this.masterAwards.find(
      (masterAward) => masterAward.masterAwardId === this.awardRequestBody?.masterAwardId,
    );
    if (!masterAward) return;

    const award: AwardGroupRef = {
      ...this.awardRequestBody,
      awardId: this.nextTemporaryId(),
      masterAwardTitle: masterAward.title,
      isCountryAward: false,
      judgeCapacity: 0,
    };

    this.addedAwards = [...this.addedAwards, award];
  }

  @action
  updateAward(): void {
    if (!this.awardRequestBody || this.editingAwardId === undefined) return;
    const body = this.awardRequestBody;
    const awardId = this.editingAwardId;
    const masterAwardTitle = this.selectedMasterAwardTitle
    if (!masterAwardTitle) return;
    const [matched, rest] = partition(this.addedAwards, (award) => award.awardId === awardId);
    if (matched.length > 0) {
      this.addedAwards = [
        ...rest,
        ...matched.map((award) => ({
          ...award,
          title: body.title,
          groupId: body.groupId,
          masterAwardId: body.masterAwardId,
          masterAwardTitle,
        })),
      ];
      return;
    }
    const original = flatMap(this.originalAwardGroups, (group) => group.awards).find((award) => award.awardId === awardId);
    if (!original || awardId === undefined) return;

    this.updatedAwardMap = new Map(this.updatedAwardMap);
    this.updatedAwardMap.set(awardId, {
      ...original,
      title: body.title,
      groupId: body.groupId,
      masterAwardId: body.masterAwardId,
      masterAwardTitle,
    });
  }

  @action
  deleteAward(): void {
    if (!this.selectedAwardGroupRef) return;
    const awardId = this.selectedAwardGroupRef.awardId;

    if (this.addedAwards.some((award) => award.awardId === awardId)) {
      this.addedAwards = this.addedAwards.filter((award) => award.awardId !== awardId);
    } else {
      this.updatedAwardMap = new Map(this.updatedAwardMap);
      this.updatedAwardMap.delete(awardId);
      this.deletedAwardIds = new Set(this.deletedAwardIds).add(awardId);
    }

    this.selectedAwardGroupRef = undefined;
  }

  @action
  restoreAward(award: AwardGroupRef): void {
    const deletedAwardIds = new Set(this.deletedAwardIds);
    deletedAwardIds.delete(award.awardId);
    this.deletedAwardIds = deletedAwardIds;
  }

  @action
  setSelectedAward(selectedAward: Award) {
    this.selectedAward = selectedAward;
  }

  @computed
  get selectedMasterAward(): MasterAward | undefined {
    if (!this.selectedAward) return undefined;
    return this.masterAwards.find((masterAward) => masterAward.masterAwardId === this.selectedAward?.masterAwardId);
  }

  @computed
  get hasChanges(): boolean {
    return (
      this.addedGroups.length > 0 ||
      this.updatedGroupMap.size > 0 ||
      this.deletedGroupIds.size > 0 ||
      this.addedAwards.length > 0 ||
      this.updatedAwardMap.size > 0 ||
      this.deletedAwardIds.size > 0
    );
  }

  /* Award Modal */

  @computed
  get selectedAwardGroupOptions(): string[] {
    if (!this.awardRequestBody) return [];
    return this.awardRequestBody.groupId ? [String(this.awardRequestBody.groupId)] : [];
  }

  @computed
  get selectedAwardGroupValue(): string {
    if (!this.awardRequestBody?.groupId) return '';
    return this.availableAwardGroups.find((group) => group.awardGroupId === this.awardRequestBody?.groupId)?.title || '';
  }

  @action
  setAwardRequestBody<K extends keyof AwardRequest>(updatedField: { key: K; value: AwardRequest[K] }): void {
    if (!this.awardRequestBody) return;

    const { key, value } = updatedField;
    const updatedAwardRequest = { ...this.awardRequestBody, [key]: value };
    this.awardRequestBody = updatedAwardRequest;
  }

  @computed
  get awardRequestValidation(): ValidationState<AwardRequest> {
    const operation = this.addOrEditAwardModalType === 'add' ? AwardOperation.Create : AwardOperation.Update;
    return createValidationState(
      getAwardRequestSchema(operation).safeParse(this.awardRequestBody),
    );
  }

  /* Group Modal */

  @action
  setGroupRequestBody<K extends keyof GroupRequest>(updatedField: { key: K; value: GroupRequest[K] }): void {
    if (!this.groupRequestBody) return;

    const { key, value } = updatedField;
    const updatedGroupRequest = { ...this.groupRequestBody, [key]: value };
    this.groupRequestBody = updatedGroupRequest;
  }

  @computed
  get groupRequestValidation(): ValidationState<GroupRequest> {
    return createValidationState(
      groupRequestSchema.safeParse(this.groupRequestBody),
    );
  }

  /* Master Award Modal */

  @action
  setSelectedMasterAwardId(masterAwardId?: number): void {
    this.selectedMasterAwardId = masterAwardId;
  }

  @action
  createMasterAwardRequestBody(): void {
    const initialValue: MasterAwardRequest = {
      title: '',
      isActive: true,
    };

    this.masterAwardRequestBody = initialValue;
  }

  @action
  createMasterAwardRequestBodyForUpdate(existingMasterAward: MasterAward): void {
    const initialValue: MasterAwardRequest = {
      title: existingMasterAward.title,
      isActive: existingMasterAward.isActive,
    };

    this.masterAwardRequestBody = initialValue;
  }

  @action
  setMasterAwardRequestBody<K extends keyof MasterAwardRequest>(updatedField: { key: K; value: MasterAwardRequest[K] }): void {
    if (!this.masterAwardRequestBody) return;

    const { key, value } = updatedField;
    const updatedMasterAwardRequest = { ...this.masterAwardRequestBody, [key]: value };
    this.masterAwardRequestBody = updatedMasterAwardRequest;
  }

  @computed
  get selectedMasterAwardOptions(): string[] {
    if (!this.awardRequestBody) return [];
    return this.awardRequestBody.masterAwardId ? [String(this.awardRequestBody.masterAwardId)] : [];
  }

  @computed
  get selectedMasterAwardValue(): string {
    if (!this.awardRequestBody?.masterAwardId) return '';
    return this.availableMasterAwards.find((masterAward) => masterAward.masterAwardId === this.awardRequestBody?.masterAwardId)?.title || '';
  }

  @computed
  get masterAwardRequestValidation(): ValidationState<MasterAwardRequest> {
    return createValidationState(
      masterAwardRequestSchema.safeParse(this.masterAwardRequestBody),
    );
  }

  /* Local helper functions */

  private nextTempId = -1;

  private nextTemporaryId(): number {
    return this.nextTempId--;
  }
}
